import React, { useState, useMemo, useRef, useLayoutEffect, useCallback, useEffect } from 'react'
import { useDebounce } from 'use-debounce'
import { styled } from '@mui/material/styles'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import SouthIcon from '@mui/icons-material/South'
import NorthIcon from '@mui/icons-material/North'

// ——— Constantes y opciones de UI ———
export const ORDER_PRODUCT = {
    pageSize: 40,
    debounceMs: 250,
    ioThrottleMs: 200,
    ioRootMargin: '0px 0px 200px 0px',
    defaultSort: { key: 'fecha', direction: 'desc' },
    apiStatusAll: [1, 2, 3],
    filtersBoxSx: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.25,
        rowGap: 1,
        mb: 1.5,
    },
    tableContainerSx: {
        maxHeight: { xs: 360, sm: 480, md: '58vh' },
        overflow: 'auto',
    },
    filterFieldSx: (extra = {}) => ({
        minWidth: 120,
        ...extra,
        '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.9)' },
    }),
}

export const ORDER_SORTABLE_COLUMNS = [
    { key: 'order', label: 'Orden', headSx: { whiteSpace: 'nowrap' } },
    { key: 'appUser', label: 'Usuario' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'notas', label: 'Notas' },
    { key: 'estado', label: 'Estado' },
    { key: 'total', label: 'Total' },
]

const NUMERIC_SORT_KEYS = new Set(['fecha', 'estado', 'total'])

// ——— Funciones puras (datos) ———
export function getDisplayOrderId(item) {
    return item.integrationId && item.integrationId !== 'XXX' ? String(item.integrationId) : String(item.webOrderId)
}

export function formatOrderFechaDisplay(item) {
    const v = item.createdOn?.slice(0, 10) ?? ''
    if (!v) return ''
    return v.split('-').reverse().join('/')
}

const parseOrderItemDate = (item) => {
    const t = item.createdOn ? Date.parse(item.createdOn) : 0
    return Number.isNaN(t) ? 0 : t
}

const sortValue = (item, colKey) => {
    switch (colKey) {
        case 'order':
            return getDisplayOrderId(item).toLowerCase()
        case 'appUser':
            return String(item.appUserId ?? '').toLowerCase()
        case 'fecha':
            return parseOrderItemDate(item)
        case 'notas':
            return (item.notes ?? '').toLowerCase()
        case 'estado':
            return Number(item.status) || 0
        case 'total':
            return Number(item.total) || 0
        default:
            return 0
    }
}

const compareSortValues = (a, b, colKey) => {
    if (NUMERIC_SORT_KEYS.has(colKey)) return Number(a) - Number(b)
    if (a < b) return -1
    if (a > b) return 1
    return 0
}

export function getNextSortState(prev, colKey) {
    const d = ORDER_PRODUCT.defaultSort
    if (prev.key !== colKey) return { key: colKey, direction: 'asc' }
    if (prev.direction === 'asc') return { key: colKey, direction: 'desc' }
    return { ...d }
}

function filterOrderRow(item, { qOrder, qUser, dateFilter, statusFilter }) {
    if (statusFilter !== 'all' && Number(item.status) !== Number(statusFilter)) return false
    if (dateFilter) {
        const d = item.createdOn?.slice(0, 10)
        if (d !== dateFilter) return false
    }
    if (qOrder) {
        const idShow = getDisplayOrderId(item).toLowerCase()
        const wid = String(item.webOrderId).toLowerCase()
        if (!idShow.includes(qOrder) && !wid.includes(qOrder)) return false
    }
    if (qUser) {
        const u = String(item.appUserId ?? '').toLowerCase()
        if (!u.includes(qUser)) return false
    }
    return true
}

export function sortOrderList(list, sortState) {
    if (!list.length) return list
    const { key, direction } = sortState
    const m = direction === 'desc' ? -1 : 1
    return [...list].sort((a, b) => m * compareSortValues(sortValue(a, key), sortValue(b, key), key))
}

export function getProcessedOrderList(orders, debounced, sortState) {
    const { order: qOrder, user: qUser, date: dateFilter, status: statusFilter } = debounced
    const filtered = orders.filter((item) =>
        filterOrderRow(item, {
            qOrder: qOrder.trim().toLowerCase(),
            qUser: qUser.trim().toLowerCase(),
            dateFilter,
            statusFilter,
        })
    )
    return sortOrderList(filtered, sortState)
}

export function getStatusLabel(status) {
    switch (Number(status)) {
        case 1:
            return 'Sin integrar'
        case 2:
            return 'Integrado'
        case 3:
            return 'Borrador'
        default:
            return ''
    }
}

const buildFilterVersionKey = (d, s) =>
    `${d.order}|${d.user}|${d.date}|${d.status}|${s.key}|${s.direction}`

// ——— Componentes estilo ———
export const OrderStyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: '#f5f5f5',
        fontSize: 15,
    },
}))

export const OrderHeadCellSort = styled(OrderStyledTableCell, {
    shouldForwardProp: (p) => p !== 'active',
})(({ active }) => ({
    transition: 'background-color 0.15s',
    ...(active ? { backgroundColor: '#2d2d2d' } : {}),
}))

// ——— Icono de encabezado (sort) ———
const iconSx = { fontSize: 16, ml: 0.25, verticalAlign: 'text-bottom' }

export function OrderProductSortHeaderIcon({ columnKey, sortState }) {
    if (sortState.key !== columnKey) {
        return <UnfoldMoreIcon sx={{ ...iconSx, opacity: 0.5 }} />
    }
    return sortState.direction === 'asc' ? <NorthIcon sx={iconSx} /> : <SouthIcon sx={iconSx} />
}

// ——— Hook: filtros, orden, paginación e IO ———
export function useOrderProductList(webOrder) {
    const [orderSearch, setOrderSearch] = useState('')
    const [userSearch, setUserSearch] = useState('')
    const [dateFilter, setDateFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [sortState, setSortState] = useState(() => ({ ...ORDER_PRODUCT.defaultSort }))

    const [debouncedOrder] = useDebounce(orderSearch, ORDER_PRODUCT.debounceMs)
    const [debouncedUser] = useDebounce(userSearch, ORDER_PRODUCT.debounceMs)

    const debounced = useMemo(
        () => ({
            order: debouncedOrder,
            user: debouncedUser,
            date: dateFilter,
            status: statusFilter,
        }),
        [debouncedOrder, debouncedUser, dateFilter, statusFilter]
    )

    const processedList = useMemo(
        () => getProcessedOrderList(webOrder, debounced, sortState),
        [webOrder, debounced, sortState]
    )
    const totalFiltered = processedList.length

    const [visibleCount, setVisibleCount] = useState(ORDER_PRODUCT.pageSize)
    const scrollContainerRef = useRef(null)
    const loadMoreSentinelRef = useRef(null)
    const lastChunkAtRef = useRef(0)

    const filterVersionKey = useMemo(
        () => buildFilterVersionKey(debounced, sortState),
        [debounced, sortState]
    )

    const resetPagination = useCallback((total) => {
        setVisibleCount(Math.min(ORDER_PRODUCT.pageSize, total))
    }, [])

    const loadNextChunk = useCallback(() => {
        setVisibleCount((c) => (c >= totalFiltered ? c : Math.min(c + ORDER_PRODUCT.pageSize, totalFiltered)))
    }, [totalFiltered])

    const visibleRows = useMemo(
        () => processedList.slice(0, visibleCount),
        [processedList, visibleCount]
    )

    const cycleSort = useCallback((key) => {
        setSortState((prev) => getNextSortState(prev, key))
    }, [])

    useLayoutEffect(() => {
        resetPagination(totalFiltered)
    }, [filterVersionKey, totalFiltered, resetPagination])

    useLayoutEffect(() => {
        const el = scrollContainerRef.current
        if (!el || visibleCount >= totalFiltered) return
        if (el.scrollHeight <= el.clientHeight + 2) loadNextChunk()
    }, [totalFiltered, visibleCount, loadNextChunk, visibleRows.length])

    useEffect(() => {
        const root = scrollContainerRef.current
        const target = loadMoreSentinelRef.current
        if (!root || !target || totalFiltered === 0 || visibleCount >= totalFiltered) return

        const obs = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return
                const now = performance.now()
                if (now - lastChunkAtRef.current < ORDER_PRODUCT.ioThrottleMs) return
                lastChunkAtRef.current = now
                loadNextChunk()
            },
            { root, rootMargin: ORDER_PRODUCT.ioRootMargin, threshold: 0 }
        )
        obs.observe(target)
        return () => obs.disconnect()
    }, [loadNextChunk, totalFiltered, visibleCount, visibleRows.length])

    const sortHeaderIsActive = (k) => sortState.key === k

    return {
        orderSearch,
        setOrderSearch,
        userSearch,
        setUserSearch,
        dateFilter,
        setDateFilter,
        statusFilter,
        setStatusFilter,
        // orden
        sortState,
        cycleSort,
        sortHeaderIsActive,
        // lista + paginación
        totalFiltered,
        visibleRows,
        visibleCount,
        scrollContainerRef,
        loadMoreSentinelRef,
    }
}
